import { useState } from "react";
import { APIError } from "@/api/error";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { error as errorToast, success as successToast } from "@/providers/ToastProvider";
import type { ImportCSVProps, ImportCSVRow, SaveResult } from "./types";

export type DrawerState = "review" | "wrong_file" | "unsaved_import";

export function useImportCsvState<T extends ImportCSVRow, TSummary>(
  props: Pick<ImportCSVProps<T, TSummary>, "onPreview" | "onSave" | "getSuccessMessage" | "actionType" | "onChange">,
) {
  const { startTracking } = useBulkActionContext();

  const [open, setOpen] = useState(false);
  const [drawerState, setDrawerState] = useState<DrawerState>("review");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<T[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const selectedRows = rows.filter((row) => selected.has(row.handle));

  const closeAll = () => {
    setOpen(false);
    setFile(null);
    setRows([]);
    setSelected(new Set());
    setDrawerState("review");
  };

  const handleFileSelected = async (nextFile: File) => {
    setIsPreviewing(true);
    try {
      const previewed = await props.onPreview(nextFile);
      setFile(nextFile);
      setRows(previewed);
      setSelected(new Set(previewed.map((row) => row.handle)));
      setDrawerState("review");
      setOpen(true);
    } catch (err) {
      if (err instanceof APIError && err.isKind("ValidationError")) {
        setFile(null);
        setDrawerState("wrong_file");
        setOpen(true);
        return;
      }
      throw err;
    } finally {
      setIsPreviewing(false);
    }
  };

  const toggleRow = (handle: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(handle)) next.delete(handle);
      else next.add(handle);
      props.onChange?.(rows.filter((row) => next.has(row.handle)));
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    const next = checked ? new Set(rows.map((row) => row.handle)) : new Set<string>();
    setSelected(next);
    props.onChange?.(checked ? rows : []);
  };

  const requestClose = () => {
    if (drawerState === "review") {
      setDrawerState("unsaved_import");
      return;
    }
    closeAll();
  };

  const keepReviewing = () => setDrawerState("review");
  const discard = () => closeAll();

  const save = async () => {
    if (!file) return;
    setIsSaving(true);
    try {
      const result: SaveResult<TSummary> = await props.onSave(selectedRows, file);
      if (result.async) {
        if (props.actionType) startTracking(result.jobId, props.actionType, null);
        closeAll();
        return;
      }
      const { async: _async, ...summary } = result;
      successToast(props.getSuccessMessage(summary as TSummary, selectedRows));
      closeAll();
    } catch {
      errorToast("Something went wrong importing — please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    open,
    drawerState,
    isPreviewing,
    isSaving,
    rows,
    selected,
    selectedRows,
    handleFileSelected,
    toggleRow,
    toggleAll,
    requestClose,
    keepReviewing,
    discard,
    save,
  };
}
